import type {
	ContentApprovalDTO,
	ContentAssignmentDTO,
	ContentDiffDTO,
	ContentInboxItemDTO,
	ContentLocalizationDTO,
	ContentProductTextsDTO,
	ContentResolutionDTO,
	ContentTextDTO,
	ContentTypeDTO,
	ContentVersionDTO,
} from '@today/types'
import { apiClient } from './api.client'
import type { Paged } from './customer.service'

/**
 * content-service API client (TO-420 content-admin-ui Contract). Request bodies
 * are snake_case — the wire shape of the `@today/models` content schemas — and
 * responses are camelCase DTOs from `@today/types`.
 */

export type Body = Record<string, unknown>

const EMPTY_PAGE = <T>(page: number, limit: number): Paged<T> => ({ data: [], total: 0, page, limit, totalPages: 0 })

/** GET a paginated list; an empty result is `200 {data:[],total:0}` (TO-632, AD-21.7). */
const getPage = async <T>(url: string, params: Record<string, string>): Promise<Paged<T>> => {
	const response = await apiClient.get<Paged<T> | ''>(url, { params })
	const page = Number(params.page ?? 1)
	const limit = Number(params.limit ?? 20)
	return response.data ? response.data : EMPTY_PAGE<T>(page, limit)
}

/** Every row of a paginated list: all pages at `limit=100`, in the API's order. */
export const getAllPages = async <T>(url: string, params: Record<string, string> = {}): Promise<T[]> => {
	const all: T[] = []
	for (let page = 1; ; page++) {
		const result = await getPage<T>(url, { ...params, page: String(page), limit: '100' })
		all.push(...result.data)
		if (page >= result.totalPages) return all
	}
}

const enc = encodeURIComponent
const text = (id: string) => `/texts/${enc(id)}`
const localization = (id: string) => `/texts/localizations/${enc(id)}`
const assignment = (id: string) => `/texts/assignments/${enc(id)}`

export const contentService = {
	allTypes: () => getAllPages<ContentTypeDTO>('/texts/types'),
	createType: async (body: Body) => (await apiClient.post<ContentTypeDTO>('/texts/types', body)).data,
	updateType: async (id: string, body: Body) =>
		(await apiClient.patch<ContentTypeDTO>(`/texts/types/${enc(id)}`, body)).data,
	deleteType: async (id: string): Promise<void> => {
		await apiClient.delete(`/texts/types/${enc(id)}`)
	},

	listTexts: (params: Record<string, string>) => getPage<ContentTextDTO>('/texts', params),
	allTexts: (params: Record<string, string>) => getAllPages<ContentTextDTO>('/texts', params),
	getText: async (id: string) => (await apiClient.get<ContentTextDTO>(text(id))).data,
	createText: async (body: Body) => (await apiClient.post<ContentTextDTO>('/texts', body)).data,
	updateText: async (id: string, body: Body) => (await apiClient.patch<ContentTextDTO>(text(id), body)).data,
	archiveText: async (id: string): Promise<void> => {
		await apiClient.delete(text(id))
	},

	versions: async (textId: string) =>
		(await apiClient.get<{ data: ContentVersionDTO[] }>(`${text(textId)}/versions`)).data.data,
	createVersion: async (textId: string, body: Body) =>
		(await apiClient.post<ContentVersionDTO>(`${text(textId)}/versions`, body)).data,
	addLocalization: async (versionId: string, body: Body) =>
		(await apiClient.post<ContentLocalizationDTO>(`/texts/versions/${enc(versionId)}/localizations`, body)).data,

	getLocalization: async (id: string) => (await apiClient.get<ContentLocalizationDTO>(localization(id))).data,
	saveLocalization: async (id: string, body: Body) =>
		(await apiClient.put<ContentLocalizationDTO>(localization(id), body)).data,
	act: async (id: string, body: Body) =>
		(await apiClient.post<ContentLocalizationDTO>(`${localization(id)}/actions`, body)).data,
	approvals: async (id: string) =>
		(await apiClient.get<{ data: ContentApprovalDTO[] }>(`${localization(id)}/approvals`)).data.data,
	diff: async (id: string, against: string) =>
		(await apiClient.get<ContentDiffDTO>(`${localization(id)}/diff`, { params: { against } })).data,

	inbox: (params: Record<string, string>) => getPage<ContentInboxItemDTO>('/texts/review-queue', params),

	allAssignments: (params: Record<string, string> = {}) =>
		getAllPages<ContentAssignmentDTO>('/texts/assignments', params),
	createAssignment: async (body: Body) => (await apiClient.post<ContentAssignmentDTO>('/texts/assignments', body)).data,
	updateAssignment: async (id: string, body: Body) =>
		(await apiClient.patch<ContentAssignmentDTO>(assignment(id), body)).data,
	removeAssignment: async (id: string): Promise<void> => {
		await apiClient.delete(assignment(id))
	},

	productTexts: async (productId: string) =>
		(await apiClient.get<ContentProductTextsDTO>(`/texts/products/${enc(productId)}`)).data,
	resolve: async (body: Body) => (await apiClient.post<ContentResolutionDTO>('/texts/resolve', body)).data,
}
